import { queryString, type CoverBand, type StockState, type StringPutUp } from '../api/client.js';

/**
 * Small shared helpers for the Products browser: the drill URL (the path rides in
 * the query string so a view is linkable and the back button works), the category
 * labels, and the days-of-cover tint classes (semantic tokens, so they read in
 * both themes).
 */

export interface ProductDrill {
  view?: 'table' | 'scatter' | undefined;
  /** The category key (a real category, or the Unassigned sentinel). */
  category?: string | undefined;
  series?: string | undefined;
  model?: string | undefined;
  /** The product identity: a child ASIN, or the productKey for an ASIN-less one. */
  asin?: string | undefined;
}

/** The Products route for a drill path; empty params drop out of the URL. */
export function productsPath(drill: ProductDrill): string {
  return `/products${queryString({
    view: drill.view,
    category: drill.category,
    series: drill.series,
    model: drill.model,
    asin: drill.asin,
  })}`;
}

/**
 * The product page for a product: `/products/<ASIN>` (the child ASIN is the
 * product), or `/products/sku/<id>` for an ASIN-less SKU. Accepts a SKU's own
 * ASIN/id too, so any SKU row can link to its product.
 */
export function productPagePath(target: { asin: string | null; skuId: number }): string {
  return target.asin
    ? `/products/${encodeURIComponent(target.asin)}`
    : `/products/sku/${target.skuId}`;
}

/**
 * The product page for a taxonomy product key (`asin:<ASIN>` or `sku:<id>`), or
 * null for a key in neither shape.
 */
export function productPagePathFromKey(productKey: string): string | null {
  if (productKey.startsWith('asin:') && productKey.length > 5) {
    return `/products/${encodeURIComponent(productKey.slice(5))}`;
  }
  const id = /^sku:(\d+)$/.exec(productKey)?.[1];
  return id ? `/products/sku/${id}` : null;
}

/** The human label for a category key (the Unassigned pseudo-category included). */
export const CATEGORY_LABEL: Record<string, string> = {
  racquet: 'Racquets',
  string: 'Strings',
  grip: 'Grips',
  footwear: 'Shoes',
  bag: 'Bags',
  other: 'Other',
  __unassigned__: 'Unassigned',
};

export function categoryLabel(key: string): string {
  return CATEGORY_LABEL[key] ?? key;
}

/** The tint classes for a days-of-cover band; a cell reads in both themes. */
export const COVER_TINT: Record<CoverBand, string> = {
  unknown: 'bg-sunken text-muted ring-line',
  critical: 'bg-danger-bg text-danger-text ring-danger-line',
  low: 'bg-warn-bg text-warn-text ring-warn-line',
  healthy: 'bg-ok-bg text-ok-text ring-ok-line',
  // Ample (90+ days) is a distinct blue, not the healthy green: plenty of runway,
  // possibly over-covered, so it reads apart from the healthy band.
  ample: 'bg-brand-50 text-brand-700 ring-brand-500',
};

/** A short label for a days-of-cover band, for the grid legend. */
export const COVER_BAND_LABEL: Record<CoverBand, string> = {
  unknown: 'No cover reading',
  critical: 'Under 14 days',
  low: '14–29 days',
  healthy: '30–89 days',
  ample: '90+ days',
};

/** A short label for a stock state. */
export const STOCK_STATE_LABEL: Record<StockState, string> = {
  in: 'In stock',
  out: 'Out of stock',
  unknown: 'Stock unknown',
};

/** How each string put-up reads (the grid's row labels, the product card, the review page). */
export const PUT_UP_LABEL: Record<StringPutUp, string> = {
  set: 'Set',
  reel: 'Reel',
  mini_reel: 'Mini reel',
  half_set: 'Half set',
};

/** A put-up label, or the "unknown" row's label. */
export function putUpLabel(putUp: StringPutUp | null): string {
  return putUp === null ? 'Put-up unknown' : PUT_UP_LABEL[putUp];
}

/** A gauge with its metric diameter when stated: "16L (1.25 mm)". */
export function gaugeLabel(gauge: string | null, gaugeMm: string | null): string {
  if (gauge === null) return '—';
  return gaugeMm ? `${gauge} (${gaugeMm} mm)` : gauge;
}

/** A put-up with its length when stated: "Reel (200 m)". */
export function putUpWithLength(putUp: StringPutUp | null, length: string | null): string {
  if (putUp === null) return '—';
  return length ? `${PUT_UP_LABEL[putUp]} (${length})` : PUT_UP_LABEL[putUp];
}
